'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/** How long the pill stays collapsed before auto-expanding. */
const COLLAPSED_DELAY_MS = 3000;
/** How long the pill stays expanded before auto-collapsing. */
const EXPANDED_DURATION_MS = 5000;

const revealedIds = new Set<string>();

const markRevealed = (photoIds: string[]): void => {
  photoIds.forEach((id) => revealedIds.add(id));
};

/** Test-only: clears the module-level reveal cache so each test starts a fresh "session". */
export const __resetRevealedIdsForTests = (): void => {
  revealedIds.clear();
};

export const useAutoReveal = (enabled: boolean, photoIds: string[]) => {
  const [expanded, setExpanded] = useState(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    const hasNewPhotos = photoIds.some((id) => !revealedIds.has(id));
    if (!hasNewPhotos) return;

    const expandTimer = setTimeout(() => {
      markRevealed(photoIds);
      setExpanded(true);

      const collapseTimer = setTimeout(() => setExpanded(false), EXPANDED_DURATION_MS);
      timersRef.current.push(collapseTimer);
    }, COLLAPSED_DELAY_MS);
    timersRef.current.push(expandTimer);

    return clearTimers;
  }, [enabled, photoIds, clearTimers]);

  /** Stop the automatic sequence and collapse — used on manual interaction. */
  const cancel = useCallback(() => {
    clearTimers();
    markRevealed(photoIds);
    setExpanded(false);
  }, [clearTimers, photoIds]);

  return { expanded, cancel };
};
